import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Grid } from '@/UI/Grid/Grid';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { FlexCenHorVer } from '@/UI/styles';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { I18N_KEYS } from '@/language/keys';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { getTimeDiffInWords } from '@/utils/date.utils';
import { toMillis } from '@/utils/folder.utils';
import { FolderCardDisplay } from '../components/FolderCardDisplay';
import { DashboardCard } from '../DashboardCard';
import type { ArchiveItem } from './archive.types';
import type { FolderWorkflowData } from '../utils/listView.utils';

const styles = stylex.create({
  // Non-interactive archive container: replaces the transparent styled(Card) with a plain box.
  container: {
    background: 'none',
    maxWidth: 345,
  },
});

interface ArchiveFolderCardProps {
  item: ArchiveItem;
  onRestore: (id: string, type: 'workflow' | 'folder') => void;
  isRestoring?: boolean;
  folderFileCount: number;
  folderData: FolderWorkflowData[string] | undefined;
}

const ArchiveFolderCard = ({ item, onRestore, isRestoring, folderFileCount, folderData }: ArchiveFolderCardProps) => {
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();

  const body = (
    <FolderCardDisplay
      id={item.id}
      name={item.name}
      posters={folderData?.posters ?? []}
      totalFiles={folderData?.count ?? 0}
      fileCount={folderFileCount}
      subtitle={
        <TextSmRg
          noWrap
          text={t(I18N_KEYS.DASHBOARD.PAGES.ARCHIVE.DELETED_AT, {
            date: getTimeDiffInWords(toMillis(item.deletedAt)),
          })}
          color={token.textSecondary}
        />
      }
      forceShowOverlay={isRestoring}
      hoverOverlay={
        <FlexCenHorVer
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: color.Black40_T,
          }}
        >
          <ButtonContained
            size="small"
            loading={isRestoring}
            onClick={() => onRestore(item.id, 'folder')}
            sx={{ pointerEvents: isRestoring ? 'none' : 'auto' }}
          >
            {t(I18N_KEYS.DASHBOARD.PAGES.ARCHIVE.RESTORE)}
          </ButtonContained>
        </FlexCenHorVer>
      }
    />
  );

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
      {isFplMigrationEnabled === true ? (
        <div {...stylex.props(styles.container)}>{body}</div>
      ) : (
        <DashboardCard>{body}</DashboardCard>
      )}
    </Grid>
  );
};

export default ArchiveFolderCard;
